/**
 * The pure pieces of the /map record layers, kept apart from the ArcGIS API so they
 * can be tested without it: marker -> graphic mapping, the summary line, the popup
 * and cluster settings, the parcel layer's fields and popup, the user's location pin, and loading the ArcGIS
 * AMD loader from Esri's CDN.
 */

/** The attributes every record pin carries; ObjectID is the FeatureLayer's id field. */
export const MARKER_FIELDS = [
  {name: "ObjectID", type: "oid"},
  {name: "title", type: "string"},
  {name: "status", type: "string"},
  {name: "detail", type: "string"},
  {name: "address", type: "string"},
  {name: "url", type: "string"}
]

/**
 * Graphic properties (for new Graphic(...)) for each marker in a markers-feed layer.
 * The object id is the pin's place in the layer: records at one business location
 * share a point, so nothing the feed sends is unique per pin. On a layer whose popup
 * has a detail row, a pin with no detail reads "Not set" rather than a blank row.
 */
export function markerGraphics(layer) {
  return layer.markers.map((marker, index) => ({
    geometry: {type: "point", longitude: marker.longitude, latitude: marker.latitude},
    attributes: {
      ObjectID: index + 1,
      title: marker.title,
      status: marker.status,
      detail: marker.detail || (layer.detail_label ? "Not set" : ""),
      address: marker.address || "",
      url: marker.url
    }
  }))
}

const formatCount = count => count.toLocaleString("en-US")

/**
 * "412 mapped · 37 without coordinates" -- the second half only when some records
 * could not be placed.
 */
export function layerSummary(layer) {
  const mapped = `${formatCount(layer.markers.length)} mapped`
  if (layer.unmapped_count === 0) { return mapped }

  return `${mapped} · ${formatCount(layer.unmapped_count)} without coordinates`
}

/**
 * The popup for one pin. ArcGIS fills the {field} placeholders from the graphic's
 * attributes; the url is absolute because its sanitizer drops relative hrefs. A layer
 * with a detail label (the license layers' "License type") gets that row first.
 */
export function popupContent(linkText, detailLabel = null) {
  const detail = detailLabel ? `<dt>${detailLabel}</dt><dd>{detail}</dd>` : ""
  return `<dl>${detail}<dt>Status</dt><dd>{status}</dd><dt>Address</dt><dd>{address}</dd></dl>` +
    `<p><a href="{url}">${linkText}</a></p>`
}

/**
 * Graphic properties for the pin marking where the user is, from a browser
 * geolocation position's coords. Blue, so it reads apart from every record layer.
 */
export function locationGraphic({latitude, longitude}) {
  return {
    geometry: {type: "point", longitude: longitude, latitude: latitude},
    symbol: {type: "simple-marker", color: [29, 78, 216], size: 12, outline: {color: "white", width: 2}},
    popupTemplate: {title: "Your location"}
  }
}

/**
 * Clusters a layer's pins when they crowd together, each cluster labelled with its
 * count. The cluster popup offers ArcGIS's "Browse features" to list its records.
 */
export function clusterConfig(label) {
  return {
    type: "cluster",
    clusterRadius: "80px",
    clusterMinSize: "24px",
    clusterMaxSize: "56px",
    popupTemplate: {
      title: label,
      content: "{cluster_count} records are here. Zoom in, or browse the records in this cluster.",
      fieldInfos: [{fieldName: "cluster_count", format: {places: 0, digitSeparator: true}}]
    },
    labelingInfo: [{
      deconflictionStrategy: "none",
      labelExpressionInfo: {expression: "Text($feature.cluster_count, '#,###')"},
      symbol: {type: "text", color: "white", font: {weight: "bold", size: "12px"}},
      labelPlacement: "center-center"
    }]
  }
}

/** Customer field names that are actually set; some customers leave address_name blank. */
const configuredFields = names => names.filter(name => typeof name === "string" && name !== "")

/** The parcel attributes to request: the configured fields, never a blank one. */
export function parcelOutFields(fields) {
  return configuredFields([fields.parcel, fields.property_card_url, fields.address_number, fields.address_name])
}

/**
 * The parcel popup: the parcel number linked to its lookup page, and the property
 * card linked by the parcel's address, built from whichever address fields are set.
 */
export function parcelPopupContent(fields, parcelLink) {
  const address = configuredFields([fields.address_number, fields.address_name]).map(name => `{${name}}`).join(" ")
  return `<dl><dt>Parcel Number</dt><dd><a href="${parcelLink}{${fields.parcel}}" target="_blank">{${fields.parcel}}</a></dd>` +
    `<dt>Property Card</dt><dd><a href="{${fields.property_card_url}}" target="_blank">${address}</a></dd></dl>`
}

/**
 * Resolves once the ArcGIS AMD loader (window.require) is on the page, adding its
 * script tag if no other map has. The tag is the same plain <script src> the other
 * map pages put in their head, so Turbo treats them as one asset and never runs the
 * loader a second time; a tag another page added is waited on rather than duplicated.
 */
export function loadArcgis(url) {
  if (window.require && typeof window.require.on === "function") { return Promise.resolve() }

  let script = Array.from(document.head.querySelectorAll("script[src]")).find(tag => tag.getAttribute("src") === url)
  if (!script) {
    script = document.createElement("script")
    script.src = url
    document.head.appendChild(script)
  }

  return new Promise((resolve, reject) => {
    script.addEventListener("load", () => resolve())
    script.addEventListener("error", () => {
      script.remove()
      reject(new Error("The ArcGIS loader could not be fetched"))
    })
  })
}
